import { useEffect, useState } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Placeholder from "@tiptap/extension-placeholder";
import {
  noteDocumentFromText,
  noteDocumentText,
  type NoteDocument,
} from "@verity-connect/domain";
import { toEditorContent, toNoteDocument } from "./note-document-bridge";

/**
 * Where a teacher writes during a session.
 *
 * This replaces a plain box, and the reason is not decoration. These notes are her record
 * of the session, the evidence behind her continuing-education credit, and soon a document
 * she hands a principal. People do not think in unbroken paragraphs, and a box that only
 * accepts one is a box that quietly asks for worse notes.
 *
 * It reports both halves on every change: the document, which is what she sees, and the
 * plain text derived from it, which is what the conflict screen, her credit evidence and
 * search all read. The two can never disagree because the second is made from the first.
 */
export function NoteEditor({ document, body, disabled, label, onChange }: {
  /** Null for a note written before documents existed; it converts from `body`. */
  document: NoteDocument | null;
  body: string;
  disabled: boolean;
  /** What a screen reader hears. Names the session, so several open notes stay apart. */
  label: string;
  onChange: (next: { document: NoteDocument; body: string }) => void;
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // Supplied separately below so their behaviour is ours to set.
        link: false,
        underline: false,
      }),
      Underline,
      Highlight,
      // The box used to say what it was for. Losing that in the move to an editor made an
      // empty note look like a broken one.
      Placeholder.configure({ placeholder: "Capture ideas, examples, and details you want to remember…" }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        // Anything else is dropped by the domain sanitiser anyway; refusing here as well
        // means she never sees a link appear and then vanish when she saves.
        protocols: ["http", "https", "mailto"],
      }),
    ],
    // A note written before documents existed converts the first time it is opened,
    // from its own text, rather than being rewritten in bulk by a migration.
    content: toEditorContent(document ?? noteDocumentFromText(body)),
    editable: !disabled,
    editorProps: {
      attributes: {
        class: "note-editor-surface",
        "aria-label": label,
      },
    },
    onUpdate: ({ editor: current }) => {
      const next = toNoteDocument(current.getJSON());
      onChange({ document: next, body: noteDocumentText(next) });
    },
  });

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  return (
    <div className="note-editor">
      <NoteToolbar editor={editor} disabled={disabled} />
      <EditorContent editor={editor} />
    </div>
  );
}

/**
 * A short row, and a panel behind one button.
 *
 * The first attempt put every control in one strip that scrolled sideways, which meant
 * links, headings and undo sat off the edge of a phone with nothing saying they existed.
 * Apple Notes solves the same problem by keeping a handful of everyday actions visible
 * and putting the rest behind "Aa", grouped — so nothing is hidden, it is just one tap
 * away and laid out where it can be read.
 *
 * The six here are the ones a teacher reaches for while somebody is talking. Everything
 * else is deliberate rather than reflexive, and can afford the tap.
 */
function NoteToolbar({ editor, disabled }: { editor: Editor; disabled: boolean }) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const chain = () => editor.chain().focus();

  return (
    <>
      <div className="note-toolbar" role="toolbar" aria-label="Formatting" aria-disabled={disabled}>
        <Button label="Bold" title="Bold" active={editor.isActive("bold")}
          onPress={() => chain().toggleBold().run()} disabled={disabled}><b>B</b></Button>
        <Button label="Italic" title="Italic" active={editor.isActive("italic")}
          onPress={() => chain().toggleItalic().run()} disabled={disabled}><i>I</i></Button>
        <Button label="Bulleted list" title="Bulleted list" active={editor.isActive("bulletList")}
          onPress={() => chain().toggleBulletList().run()} disabled={disabled}>•</Button>
        <Button label="Numbered list" title="Numbered list" active={editor.isActive("orderedList")}
          onPress={() => chain().toggleOrderedList().run()} disabled={disabled}>1.</Button>
        <Button label="Checklist" title="Checklist" active={editor.isActive("taskList")}
          onPress={() => chain().toggleTaskList().run()} disabled={disabled}>☑</Button>
        <Button label="Link" title="Link" active={editor.isActive("link")}
          onPress={() => addLink(editor)} disabled={disabled}>🔗</Button>
        <Button label="More formatting" title="More formatting" active={sheetOpen}
          onPress={() => setSheetOpen((open) => !open)} disabled={disabled}>Aa</Button>
      </div>
      {sheetOpen && <NoteFormatSheet editor={editor} disabled={disabled} onClose={() => setSheetOpen(false)} />}
    </>
  );
}

/**
 * Everything else, grouped so it can be read rather than hunted for.
 *
 * Opens below the toolbar rather than over the note, because a panel that covers what she
 * is writing makes her guess at what she is formatting.
 */
function NoteFormatSheet({ editor, disabled, onClose }: {
  editor: Editor;
  disabled: boolean;
  onClose: () => void;
}) {
  const chain = () => editor.chain().focus();
  return (
    <div className="note-format-sheet" role="group" aria-label="More formatting">
      <section>
        <h4>Style</h4>
        <div>
          <Wide label="Heading" active={editor.isActive("heading", { level: 2 })}
            onPress={() => chain().toggleHeading({ level: 2 }).run()} disabled={disabled} />
          <Wide label="Subheading" active={editor.isActive("heading", { level: 3 })}
            onPress={() => chain().toggleHeading({ level: 3 }).run()} disabled={disabled} />
          <Wide label="Quote" active={editor.isActive("blockquote")}
            onPress={() => chain().toggleBlockquote().run()} disabled={disabled} />
          <Wide label="Code" active={editor.isActive("codeBlock")}
            onPress={() => chain().toggleCodeBlock().run()} disabled={disabled} />
        </div>
      </section>
      <section>
        <h4>Emphasis</h4>
        <div>
          <Wide label="Underline" active={editor.isActive("underline")}
            onPress={() => chain().toggleUnderline().run()} disabled={disabled} />
          <Wide label="Strikethrough" active={editor.isActive("strike")}
            onPress={() => chain().toggleStrike().run()} disabled={disabled} />
          <Wide label="Highlight" active={editor.isActive("highlight")}
            onPress={() => chain().toggleHighlight().run()} disabled={disabled} />
        </div>
      </section>
      <section>
        <h4>Lists</h4>
        <div>
          <Wide label="Indent" onPress={() => chain().sinkListItem("listItem").run()}
            disabled={disabled || !editor.can().sinkListItem("listItem")} />
          <Wide label="Outdent" onPress={() => chain().liftListItem("listItem").run()}
            disabled={disabled || !editor.can().liftListItem("listItem")} />
          <Wide label="Divider" onPress={() => chain().setHorizontalRule().run()} disabled={disabled} />
        </div>
      </section>
      <section>
        <h4>Undo</h4>
        <div>
          <Wide label="Undo" onPress={() => chain().undo().run()}
            disabled={disabled || !editor.can().undo()} />
          <Wide label="Redo" onPress={() => chain().redo().run()}
            disabled={disabled || !editor.can().redo()} />
        </div>
      </section>
      <button type="button" className="note-format-sheet-close" onClick={onClose}>Done</button>
    </div>
  );
}

/** A named button in the panel, where there is room for the word rather than a symbol. */
function Wide({ label, active, disabled, onPress }: {
  label: string;
  active?: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      className={active ? "is-active" : undefined}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onPress}
      disabled={disabled}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

/**
 * Asking for the address rather than guessing it.
 *
 * A prompt is plain, but it works on a phone keyboard and inside both native shells,
 * which a floating panel would have to be built and tested for on each.
 */
function addLink(editor: Editor): void {
  const existing = editor.getAttributes("link").href as string | undefined;
  const entered = window.prompt("Link address", existing ?? "https://");
  if (entered === null) return;
  if (!entered.trim()) {
    editor.chain().focus().unsetLink().run();
    return;
  }
  editor.chain().focus().extendMarkRange("link").setLink({ href: entered.trim() }).run();
}

function Button({ label, title, active, disabled, onPress, children }: {
  label: string;
  title: string;
  active?: boolean;
  disabled: boolean;
  onPress: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={active ? "is-active" : undefined}
      // The editor must not lose the selection when a button takes focus, or every
      // action would apply to nothing.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onPress}
      disabled={disabled}
      aria-pressed={active}
      aria-label={label}
      title={title}
    >
      {children}
    </button>
  );
}
